import {
  INCIDENT_GRAPHQL_DETAIL,
  INCIDENT_DEFAULT_TICKET_FIELDS,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import type { IncidentV2Summary } from '../types';
import {
  createIncidentSummary,
  updateIncidentSummary,
  deleteIncidentSummary,
} from '../api';

const USER_SUMMARY_TYPE = 'USER_SUMMARY';
const USER_SUMMARY_TYPE_API = 'user_summary';

type UseIncidentSummaryParams = {
  incidentId: string;
  summaries: IncidentV2Summary[];
};

const useIncidentSummary = ({
  incidentId,
  summaries,
}: UseIncidentSummaryParams) => {
  const selectedEnvId = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();

  const userSummary = summaries.find(
    (s) => s.summaryType === USER_SUMMARY_TYPE,
  );

  const invalidateIncidentDetails = () => {
    queryClient.invalidateQueries([INCIDENT_GRAPHQL_DETAIL, incidentId], {
      exact: false,
    });
    queryClient.invalidateQueries(
      [INCIDENT_DEFAULT_TICKET_FIELDS, selectedEnvId, incidentId],
      { exact: false },
    );
  };

  const canEdit = hasPermission('EDIT_INCIDENTS');

  const { mutateAsync: saveSummary, isLoading: isSaving } = useMutation(
    async (content: string) => {
      if (!selectedEnvId || !canEdit) return;

      if (userSummary) {
        return updateIncidentSummary(
          selectedEnvId,
          incidentId,
          USER_SUMMARY_TYPE_API,
          content,
        );
      }
      return createIncidentSummary(
        selectedEnvId,
        incidentId,
        USER_SUMMARY_TYPE_API,
        content,
      );
    },
    {
      onSuccess: invalidateIncidentDetails,
    },
  );

  const { mutateAsync: removeSummary, isLoading: isDeleting } = useMutation(
    async () => {
      if (!selectedEnvId || !userSummary || !canEdit) return;

      return deleteIncidentSummary(
        selectedEnvId,
        incidentId,
        USER_SUMMARY_TYPE_API,
      );
    },
    {
      onSuccess: invalidateIncidentDetails,
    },
  );

  return {
    userSummary,
    saveSummary,
    removeSummary,
    isSaving,
    isDeleting,
    canEdit,
  };
};

export default useIncidentSummary;
